import { DateTime } from 'luxon';
import { DateRange } from 'src/app/common/daterange';
import { buildDemoReportUsers, filterDemoReportUsers } from './demo-report-users.mock';
import { countDemoWorkdays, getDemoDateBuckets, withBucketDateOptions } from './demo-date-range.mock';
import { GetStatsOptions, NewStatsOptions, StatsTotal } from '../stats/stats.service';
import { User } from 'src/models';

export interface DemoReportProject {
  id: string;
  name: string;
  weight: number;
}

export interface DemoReportTask {
  id: string;
  name: string;
  projectId: string;
}

/** Aligned with {@link DemoService} project distribution. */
export const DEMO_REPORT_PROJECTS: DemoReportProject[] = [
  { id: 'Z_xiqjD8_Rus64aR', name: 'General Work', weight: 0.15 },
  { id: 'Y_lmn45P_Qrt78bS', name: 'Marketing Campaign', weight: 0.25 },
  { id: 'X_opq23R_Pst91cT', name: 'UI/UX Redesign', weight: 0.10 },
  { id: 'W_uvw67S_Ouv04dU', name: 'Backend Migration', weight: 0.30 },
  { id: 'V_xyz89T_Nwx17eV', name: 'Customer Onboarding', weight: 0.20 },
];

export const DEMO_REPORT_TASKS: DemoReportTask[] = [
  { id: 'demo-task-gw-1', name: 'Weekly planning', projectId: 'Z_xiqjD8_Rus64aR' },
  { id: 'demo-task-gw-2', name: 'Administrative work', projectId: 'Z_xiqjD8_Rus64aR' },
  { id: 'demo-task-mc-1', name: 'Content creation', projectId: 'Y_lmn45P_Qrt78bS' },
  { id: 'demo-task-mc-2', name: 'Social media', projectId: 'Y_lmn45P_Qrt78bS' },
  { id: 'demo-task-ui-1', name: 'Wireframes', projectId: 'X_opq23R_Pst91cT' },
  { id: 'demo-task-ui-2', name: 'Design review', projectId: 'X_opq23R_Pst91cT' },
  { id: 'demo-task-be-1', name: 'API refactor', projectId: 'W_uvw67S_Ouv04dU' },
  { id: 'demo-task-be-2', name: 'Database migration', projectId: 'W_uvw67S_Ouv04dU' },
  { id: 'demo-task-co-1', name: 'Documentation', projectId: 'V_xyz89T_Nwx17eV' },
  { id: 'demo-task-co-2', name: 'Training sessions', projectId: 'V_xyz89T_Nwx17eV' },
];

export type MockProjectsReportRow = {
  total: number;
  userId?: string;
  userName?: string;
  projectId?: string;
  projectName?: string;
  taskId?: string;
  taskName?: string;
  date?: string;
};

export interface DemoProjectsReportSeed {
  int(min: number, max: number, ...scope: (string | number)[]): number;
}

function filterUsers(options: NewStatsOptions): User[] {
  return filterDemoReportUsers(buildDemoReportUsers(), {
    user: options.user,
    tag: options.tag,
  });
}

/** `prefilter[projectId]` is a scalar for a drilldown and an array for the report filter. */
function projectFilterIds(options: NewStatsOptions): string[] | null {
  const projectFilter = options['prefilter[projectId]'];
  if (!projectFilter) { return null; }
  return Array.isArray(projectFilter) ? projectFilter : [projectFilter];
}

function filterProjects(options: NewStatsOptions): DemoReportProject[] {
  let projects = [...DEMO_REPORT_PROJECTS];

  const projectIds = projectFilterIds(options);
  if (projectIds) {
    projects = projects.filter(project => projectIds.includes(project.id));
  }

  if (options.task && options.task !== '*') {
    const task = DEMO_REPORT_TASKS.find(item => item.id === options.task);
    projects = task ? projects.filter(project => project.id === task.projectId) : [];
  }

  return projects;
}

function filterTasks(options: NewStatsOptions, projects: DemoReportProject[]): DemoReportTask[] {
  const projectIds = new Set(projects.map(project => project.id));
  let tasks = DEMO_REPORT_TASKS.filter(task => projectIds.has(task.projectId));

  const taskFilter = options['prefilter[taskId]'];
  if (taskFilter) {
    tasks = tasks.filter(task => task.id === taskFilter);
  }

  if (options.task && options.task !== '*') {
    tasks = tasks.filter(task => task.id === options.task);
  }

  return tasks;
}

function periodTotalSeconds(options: NewStatsOptions, userCount: number): number {
  const from = DateTime.fromISO(options.from || DateTime.now().toISO());
  const to = DateTime.fromISO(options.to || DateTime.now().toISO());
  const workdays = countDemoWorkdays(from, to);
  const baseDailySeconds = 28_800;
  return baseDailySeconds * Math.max(1, userCount) * workdays;
}

function rowTotal(
  seed: DemoProjectsReportSeed,
  scope: (string | number)[],
  share: number,
): number {
  const variation = seed.int(85, 115, 'projects-report-total', ...scope) / 100;
  return Math.max(60, Math.round(share * variation));
}

export function buildMockProjectsReportStats(
  seed: DemoProjectsReportSeed,
  options: NewStatsOptions,
): MockProjectsReportRow[] {
  const groupFields = String(options['group-by'] || 'projectId').split(',').filter(Boolean);
  const users = filterUsers(options);
  const projects = filterProjects(options);
  const tasks = filterTasks(options, projects);
  const periodTotal = periodTotalSeconds(options, users.length);

  if (!projects.length || !users.length) {
    return [];
  }

  const includeDate = groupFields.includes('date');
  const includeUser = groupFields.includes('userId');
  const includeProject = groupFields.includes('projectId');
  const includeTask = groupFields.includes('taskId');

  const dateBuckets = includeDate
    ? getDemoDateBuckets({
      ...options,
      period: options.period || 'days',
      interval: options.interval ?? 1,
    })
    : [null];

  const rows: MockProjectsReportRow[] = [];

  if (groupFields.length === 1 && groupFields[0] === 'projectId') {
    return projects.map(project => ({
      projectId: project.id,
      projectName: project.name,
      total: rowTotal(seed, [project.id], periodTotal * project.weight),
    })).filter(row => row.total >= 1);
  }

  if (groupFields.length === 1 && groupFields[0] === 'taskId') {
    return tasks.map(task => {
      const project = projects.find(item => item.id === task.projectId);
      const tasksInProject = tasks.filter(item => item.projectId === project.id).length || 1;
      const projectShare = periodTotal * project.weight;
      return {
        taskId: task.id,
        taskName: task.name,
        projectId: project.id,
        total: rowTotal(seed, [task.id], projectShare / tasksInProject),
      };
    }).filter(row => row.total >= 1);
  }

  if (groupFields.length === 1 && groupFields[0] === 'userId') {
    const projectFilter = projectFilterIds(options);
    const taskFilter = options['prefilter[taskId]'];
    // Only a single selected project can scope the per-user share; a multi-project filter has
    // already narrowed `projects`, so leave the share spread across them.
    const project = projectFilter?.length === 1
      ? projects.find(item => item.id === projectFilter[0])
      : null;
    const task = taskFilter ? tasks.find(item => item.id === taskFilter) : null;
    const scopedProject = project || (task ? projects.find(item => item.id === task.projectId) : null);

    return users.map(user => {
      let share = periodTotal / Math.max(1, users.length);
      if (scopedProject) {
        const tasksInProject = tasks.filter(item => item.projectId === scopedProject.id).length || 1;
        share = (periodTotal * scopedProject.weight) / Math.max(1, users.length);
        if (task) {
          share = share / tasksInProject;
        }
      }

      return {
        userId: user.id,
        userName: user.name,
        total: rowTotal(seed, [user.id, scopedProject?.id || 'all', task?.id || 'all'], share),
      };
    }).filter(row => row.total >= 1);
  }

  for (const bucket of dateBuckets) {
    const bucketOptions = bucket ? withBucketDateOptions(options, bucket) : options;
    const bucketUsers = filterUsers(bucketOptions);
    const bucketTotal = periodTotalSeconds(bucketOptions, bucketUsers.length);

    for (const project of projects) {
      const projectTasks = tasks.filter(task => task.projectId === project.id);
      const tasksForProject = includeTask ? projectTasks : [null];

      for (const task of tasksForProject) {
        const taskShare = includeTask
          ? (bucketTotal * project.weight) / Math.max(1, projectTasks.length)
          : bucketTotal * project.weight;

        for (const user of (includeUser ? bucketUsers : [null])) {
          const userShare = includeUser
            ? taskShare / Math.max(1, bucketUsers.length)
            : taskShare;

          const row: MockProjectsReportRow = {
            total: rowTotal(
              seed,
              [
                user?.id || 'all',
                project.id,
                task?.id || 'all',
                bucket?.start.toISODate() || 'all',
              ],
              userShare,
            ),
          };

          if (includeProject) {
            row.projectId = project.id;
            row.projectName = project.name;
          }
          if (includeTask && task) {
            row.taskId = task.id;
            row.taskName = task.name;
          }
          if (includeUser && user) {
            row.userId = user.id;
            row.userName = user.name;
          }
          if (includeDate && bucket) {
            row.date = bucket.start.toUTC().toISO();
          }

          if (row.total >= 1) {
            rows.push(row);
          }
        }
      }
    }
  }

  return rows;
}

export function isProjectsReportGroupBy(groupBy: string | undefined, options?: NewStatsOptions): boolean {
  const fields = (groupBy || '').split(',').filter(Boolean);
  if (fields.some(field => field === 'projectId' || field === 'taskId')) {
    return true;
  }
  return fields.length === 1 && fields[0] === 'userId'
    && !!(options?.['prefilter[projectId]'] || options?.['prefilter[taskId]']);
}

/** Top tasks on the individual dashboard — aligned with {@link DEMO_REPORT_TASKS} / {@link DEMO_REPORT_PROJECTS}. */
export function buildDemoDashboardTaskTotals(totalSeconds: number, limit = 5): StatsTotal[] {
  return DEMO_REPORT_TASKS
    .map(task => {
      const project = DEMO_REPORT_PROJECTS.find(item => item.id === task.projectId);
      if (!project) {
        return null;
      }
      const tasksInProject = DEMO_REPORT_TASKS.filter(item => item.projectId === project.id).length || 1;
      return {
        id: task.id,
        name: task.name,
        total: Math.round((totalSeconds * project.weight) / tasksInProject),
        project: project.id,
        projectName: project.name,
      } as StatsTotal;
    })
    .filter((task): task is StatsTotal => task != null)
    .sort((a, b) => b.total - a.total)
    .slice(0, limit);
}

/** Task totals for stats APIs (projects report, activity summary, etc.). */
export function buildMockTasksTotalStats(
  seed: DemoProjectsReportSeed,
  options: GetStatsOptions,
): StatsTotal[] {
  const rows = buildMockProjectsReportStats(seed, {
    ...(options as NewStatsOptions),
    'group-by': 'taskId',
  });

  return rows
    .map(row => {
      const project = DEMO_REPORT_PROJECTS.find(item => item.id === row.projectId);
      return {
        id: row.taskId,
        name: row.taskName,
        total: row.total,
        project: row.projectId,
        projectName: project?.name,
        userId: typeof options.user === 'string' ? options.user : undefined,
      } as StatsTotal;
    })
    .sort((a, b) => b.total - a.total);
}
